import { PlayerContent } from '@/components/players/PlayerContent';
import { BulkCreateUsersContent } from '@/components/players/BulkCreateUsersContent';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { PlayerService } from '@/lib/services/player';
import { toast } from 'sonner';
import { useListRolesQuery, RoleDto } from '@/store/apiSlice';
import { Switch } from '@/components/ui/switch';

export default function PlayersPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    password: '',
    password_confirm: '',
  });
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');
  const [flags, setFlags] = useState({
    is_active: true,
    is_verified: false,
    is_superuser: false,
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: rolesData } = useListRolesQuery();
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<{
    total: number;
    success: number;
    failed: { rowNumber: number; username: string; email: string; error: string }[];
  } | null>(null);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: [] }));
  };

  const handleCreateUser = async () => {
    if (
      !form.username.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.password_confirm ||
      !selectedRoleId
    ) {
      toast.error('Username, email, password, and team are required.');
      return;
    }
    try {
      setIsSubmitting(true);
      await PlayerService.create({
        ...form,
        team_id: selectedRoleId ? Number(selectedRoleId) : undefined,
        ...flags,
      });
      toast.success('User created successfully.');
      setIsCreateOpen(false);
      setForm({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        password: '',
        password_confirm: '',
      });
      setSelectedRoleId('');
      setFlags({
        is_active: true,
        is_verified: false,
        is_superuser: false,
      });
      setErrors({});
    } catch (e: any) {
      console.error(e);
      const data = e?.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrors: Record<string, string[]> = {};
        Object.entries(data).forEach(([field, value]) => {
          if (Array.isArray(value)) {
            fieldErrors[field] = (value as any[]).map((msg) => String(msg));
          } else if (value) {
            fieldErrors[field] = [String(value)];
          }
        });
        setErrors(fieldErrors);
        // Also show a summary toast
        const messages: string[] = [];
        Object.entries(fieldErrors).forEach(([field, msgs]) => {
          msgs.forEach((msg) => messages.push(`${field}: ${msg}`));
        });
        if (messages.length) {
          toast.error(messages.join('\n'));
        }
      } else {
        toast.error('Failed to create user.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Players Dashboard
          </h1>
          <p className="text-muted-foreground">
            Track and manage player performance, statistics, and engagement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setIsBulkOpen(true)}>
            Bulk Create Users
          </Button>
          <Button type="button" size="sm" onClick={() => setIsCreateOpen(true)}>
            + Create User
          </Button>
        </div>
      </div>

      <PlayerContent />

      {/* Bulk create users dialog */}
      <Dialog
        open={isBulkOpen}
        onOpenChange={(open) => {
          setIsBulkOpen(open);
          if (!open) {
            setBulkSummary(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Create Users</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Upload a CSV file using the provided template to create multiple users at once.
            </p>
          </DialogHeader>
          <BulkCreateUsersContent
            roles={rolesData as RoleDto[] | undefined}
            summary={bulkSummary}
            setSummary={setBulkSummary}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) {
            setForm({
              username: '',
              email: '',
              first_name: '',
              last_name: '',
              phone_number: '',
              password: '',
              password_confirm: '',
            });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new user</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Fill in basic details, assign a team, and set initial access level.
            </p>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Identity */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Identity
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Username</p>
                <Input
                  value={form.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="e.g. zuhaib.z"
                />
                {errors.username && errors.username.length > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.username[0]}</p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Email</p>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="user@example.com"
                />
                {errors.email && errors.email.length > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.email[0]}</p>
                )}
              </div>
              </div>
            </div>

            {/* Contact & Team */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Contact & Team
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">First name</p>
                  <Input
                    value={form.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Last name</p>
                  <Input
                    value={form.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Phone number</p>
                  <Input
                    value={form.phone_number}
                    onChange={(e) => handleChange('phone_number', e.target.value)}
                    placeholder="+91..."
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Team <span className="text-red-500">*</span>
                  </p>
                  <select
                    className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                    value={selectedRoleId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedRoleId(v ? Number(v) : '');
                    }}
                  >
                    <option value="">Select team</option>
                    {(rolesData as RoleDto[] | undefined)?.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name || role.code}
                      </option>
                    ))}
                  </select>
                  {(errors.team_id?.[0] ?? errors.role_id?.[0]) && (
                    <p className="text-xs text-red-500 mt-0.5">
                      {errors.team_id?.[0] ?? errors.role_id?.[0]}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Status & Permissions */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Status & permissions
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Active</span>
                  <Switch
                    checked={flags.is_active}
                    onCheckedChange={(checked: boolean) =>
                      setFlags((prev) => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Verified</span>
                  <Switch
                    checked={flags.is_verified}
                    onCheckedChange={(checked: boolean) =>
                      setFlags((prev) => ({ ...prev, is_verified: checked }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Security
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Password</p>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Enter password"
                  />
                  {errors.password && errors.password.length > 0 && (
                    <p className="text-xs text-red-500 mt-0.5">{errors.password[0]}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Confirm password</p>
                  <Input
                    type="password"
                    value={form.password_confirm}
                    onChange={(e) => handleChange('password_confirm', e.target.value)}
                    placeholder="Re-enter password"
                  />
                  {errors.password_confirm && errors.password_confirm.length > 0 && (
                    <p className="text-xs text-red-500 mt-0.5">
                      {errors.password_confirm[0]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsCreateOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleCreateUser}
              disabled={
                isSubmitting ||
                !form.username.trim() ||
                !form.email.trim() ||
                !form.password ||
                !form.password_confirm
              }
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
